// src/components/gestionEvento/integraciones/EventManagerFiles.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Button, CircularProgress, Link as MuiLink, Alert, LinearProgress,
  Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CheckCircleOutline as CheckIcon,
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useEventContext } from '../../../context/EventContext';
import { useAuth } from '../../../context/AuthContext';
import { storage } from '../../../firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { Timestamp } from 'firebase/firestore';
import {
  parseEvtFile,
  parsePplFile,
  parseSchFile,
  sanitizeText
} from './fileUploadUtils';

const FILE_TYPES = [
  { id: 'evt', label: 'Eventos (.evt)', accept: '.evt' },
  { id: 'ppl', label: 'Atletas (.ppl)',  accept: '.ppl' },
  { id: 'sch', label: 'Horario (.sch)',  accept: '.sch' },
];

export default function EventManagerFiles() {
  const {
    competitionId,
    fileMetadata,
    setEvtData,
    setPplData,
    setSchData,
    setManualUpload,
    updateFileMetadata,
    deleteFileMetadata,
    refreshFiles,
    TrackEventCodes,
    fieldHorizontalEvents,
    fieldVerticalEvents,
    loading: contextLoading
  } = useEventContext();

  const { user } = useAuth();

  // Derivar dinámicamente archivos de campo
  // [ ['docId', meta], ... ]
  const fieldEntries = useMemo(() =>
    Object.entries(fileMetadata).filter(([,m]) => m.isFieldEventFile),
  [fileMetadata]);

  // Separar EVT de SCH de campo
  const [ evtFieldEntry ] = fieldEntries.filter(([,m]) => m.name.toLowerCase().endsWith('.evt'));
  const [ schFieldEntry ] = fieldEntries.filter(([,m]) => m.name.toLowerCase().endsWith('.sch'));

  const evtFieldId = evtFieldEntry?.[0];
  const fieldEvtMetadata = evtFieldEntry?.[1] ?? null;

  const schFieldId = schFieldEntry?.[0];
  const fieldSchMetadata = schFieldEntry?.[1] ?? null;

  // Checkbox controla sólo la UI, NO borra metadatos
  const [evtIncludesFieldEvents, setEvtIncludesFieldEvents] = useState(true);
  useEffect(() => {
    setEvtIncludesFieldEvents(!fieldEvtMetadata);
  }, [fieldEvtMetadata]);

  // Estados upload/delete
  const [isUploading, setIsUploading]               = useState(null);
  const [uploadProgress, setUploadProgress]         = useState(0);
  const [isUploadingField, setIsUploadingField]     = useState(null);
  const [fieldUploadProgress, setFieldUploadProgress] = useState(0);
  const [isDeleting, setIsDeleting]                 = useState(null);
  const [actionError, setActionError]               = useState(null);
  const [actionSuccess, setActionSuccess]           = useState(null);

  // Diálogo de confirmación
  const [confirmOpen, setConfirmOpen]               = useState(false);
  const [deleteTarget, setDeleteTarget]             = useState({ id:null, name:'', isField:false });

  const openConfirm = (id, name, isField=false) => {
    setDeleteTarget({ id, name, isField });
    setConfirmOpen(true);
  };
  const closeConfirm = () => {
    setConfirmOpen(false);
    setDeleteTarget({ id:null, name:'', isField:false });
  };
  const handleConfirmDelete = async () => {
    const { id, name, isField } = deleteTarget;
    closeConfirm();
    setIsDeleting(id);
    try {
      // 1) Borrar en Storage
      const path = `usuarios/${user.uid}/competencias/${competitionId}/datos/${name}`;
      await deleteObject(ref(storage,path)).catch(e => {
        if (e.code !== 'storage/object-not-found') throw e;
      });
      // 2) Borrar metadatos Firestore
      await deleteFileMetadata(id);
      // 3) Limpiar datos locales
      if (isField) {
        if (id === evtFieldId) setEvtData([]);
        else if (id === schFieldId) setSchData([]);
      } else {
        if (id === 'evt')      setEvtData([]);
        else if (id === 'ppl') setPplData([]);
        else if (id === 'sch') setSchData([]);
      }
      setActionSuccess(`${name} eliminado.`);
    } catch(err) {
      setActionError(`Error eliminando: ${err.message}`);
    } finally {
      setIsDeleting(null);
    }
  };

  // Subida genérica (.evt, .ppl, .sch)
  const handleFileUpload = async (e, ft) => {
    const file = e.target.files[0]; e.target.value = null;
    if (!file) return;
    setActionError(null); setActionSuccess(null);
    setIsUploading(ft.id); setUploadProgress(0);

    if (!file.name.toLowerCase().endsWith(ft.accept)) {
      setActionError(`Inválido: ${ft.accept}`); setIsUploading(null); return;
    }
    if (!competitionId || !user?.uid) {
      setActionError("Error: sin IDs válidos."); setIsUploading(null); return;
    }

    try {
      let parsed = null;
      if (['evt','ppl','sch'].includes(ft.id)) {
        const txt = await file.text();
        const s   = sanitizeText(txt);
        if (!s.trim()) throw new Error('Archivo vacío.');
        parsed = ft.id === 'evt'
          ? parseEvtFile(s,TrackEventCodes,fieldHorizontalEvents,fieldVerticalEvents)
          : ft.id === 'ppl'
            ? parsePplFile(s)
            : parseSchFile(s);
      }

      const path = `usuarios/${user.uid}/competencias/${competitionId}/datos/${file.name}`;
      const task = uploadBytesResumable(ref(storage,path),file);
      const url = await new Promise((res,rej) => {
        task.on('state_changed',
          snap => setUploadProgress(Math.round(snap.bytesTransferred/snap.totalBytes*100)),
          rej,
          () => getDownloadURL(task.snapshot.ref).then(res).catch(rej)
        );
      });

      const meta = { url, name:file.name, timestamp:Timestamp.now() };
      const { success } = await updateFileMetadata(ft.id, meta);
      if (!success) throw new Error('Error guardando metadatos');

      if (parsed) {
        if (ft.id==='evt') setEvtData(parsed);
        else if (ft.id==='ppl') setPplData(parsed);
        else if (ft.id==='sch') setSchData(parsed);
        setManualUpload(true);
      }
      setActionSuccess(`${file.name} subido.`);
    } catch(err) {
      setActionError(`Error: ${err.message}`);
    } finally {
      setIsUploading(null);
      setUploadProgress(0);
    }
  };

  // Subida de campo
  const handleFieldFileUpload = async (e, fieldId) => {
    const file = e.target.files[0]; e.target.value = null;
    if (!file) return;
    setActionError(null); 
    setActionSuccess(null);
    setIsUploadingField(fieldId); 
    setFieldUploadProgress(0);
  
    // --- 1) Validar extensión usando tu FILE_TYPES.accept dinámicamente ---
    //   buscamos la entrada correspondiente en tu FILE_TYPES extendido:
    const mapping = {
      // aquí asumimos que tus IDs de campo coinciden:
      fldlynx_evt: '.evt',
      fldlynx_sch: '.sch'
    };
    const expectedExt = mapping[fieldId];
    if (!file.name.toLowerCase().endsWith(expectedExt)) {
      setActionError(`Inválido: ${expectedExt}`);
      setIsUploadingField(null);
      return;
    }
  
    if (!competitionId || !user?.uid) {
      setActionError("Error: sin IDs válidos.");
      setIsUploadingField(null);
      return;
    }
  
    try {
      // 1) Leer y parsear directamente
      const raw = await file.text();
      const txt = sanitizeText(raw);
      if (!txt.trim()) throw new Error('Archivo vacío.');

      // 2) Subir a Storage
      const path = `usuarios/${user.uid}/competencias/${competitionId}/datos/${file.name}`;
      const task = uploadBytesResumable(ref(storage,path),file);
      const url  = await new Promise((res, rej) => {
        task.on('state_changed',
          snap => setFieldUploadProgress(Math.round(snap.bytesTransferred/snap.totalBytes*100)),
          rej,
          () => getDownloadURL(task.snapshot.ref).then(res).catch(rej)
        );
      });


       // 3) Guardar metadatos **con** isFieldEventFile: true
       const meta = {
         url,
         name: file.name,
         timestamp: Timestamp.now(),
         isFieldEventFile: true,
         type: 'datos',
         originalPath: file.name
       };

      const { success } = await updateFileMetadata(fieldId, meta);
      if (!success) throw new Error('Error guardando metadatos campo');

      // 4) Aplicar parseo local (ya no hace fetch remotos)
      if (fieldId === evtFieldId) {
        const parsed = parseEvtFile(txt, TrackEventCodes, fieldHorizontalEvents, fieldVerticalEvents);
        setEvtData(parsed);
      } else {
        const parsed = parseSchFile(txt);
        setSchData(parsed);
      }
      setManualUpload(true);

      setActionSuccess(`${file.name} subido y parseado.`);
    } catch (err) {
      setActionError(`Error: ${err.message}`);
    } finally {
      setIsUploadingField(null);
      setFieldUploadProgress(0);
    }
  };

  // Cambiar checkbox no borra nada, sólo controla UI
  const handleCheckboxChange = () => {
    setEvtIncludesFieldEvents(prev => !prev);
  };

  return (
    <Box>
      {/* Refrescar */}
      <Box mb={2} display="flex" justifyContent="flex-end">
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={refreshFiles}
          disabled={contextLoading}
        >
          Refrescar archivos
        </Button>
      </Box>

      {/* Checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={evtIncludesFieldEvents}
            onChange={handleCheckboxChange}
            disabled={contextLoading}
            size="small"
          />
        }
        label="Archivo .evt principal incluye eventos de campo"
        sx={{ mb:1 }}
      />

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th':{ fontWeight:'bold' } }}>
                <TableCell>Tipo</TableCell>
                <TableCell>Archivo</TableCell>
                <TableCell>Últ. sinc.</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Base */}
              {FILE_TYPES.map(ft => {
                const md = fileMetadata[ft.id];
                const uploading = isUploading===ft.id;
                const deleting  = isDeleting===ft.id;
                const disabledAll = !!isUploading||!!isDeleting||contextLoading;
                return (
                  <TableRow key={ft.id}>
                    <TableCell>{ft.label}</TableCell>
                    <TableCell>
                      {md?.url
                        ? <Tooltip title={md.name}>
                            <MuiLink href={md.url} target="_blank">
                              <CheckIcon color="success" fontSize="inherit" sx={{mr:0.5}}/>
                              {md.name}
                            </MuiLink>
                          </Tooltip>
                        : <Typography color="text.secondary">No subido</Typography>
                      }
                      {uploading && (
                        <Box mt={0.5}>
                          <LinearProgress variant="determinate" value={uploadProgress} sx={{height:3}}/>
                          <Typography variant="caption" textAlign="right">{uploadProgress}%</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {md?.timestamp instanceof Date ? md.timestamp.toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Button
                          variant={md?'outlined':'contained'}
                          size="small" component="label"
                          startIcon={uploading? <CircularProgress size={16}/> : <UploadFileIcon/>}
                          disabled={disabledAll}
                        >
                          {md?'Reemplazar':'Subir'}
                          <input type="file" accept={ft.accept} hidden onChange={e=>handleFileUpload(e,ft)}/>
                        </Button>
                        <IconButton href={md?.url} target="_blank" disabled={!md?.url||disabledAll}>
                          <DownloadIcon/>
                        </IconButton>
                        <IconButton
                          color="error"
                          disabled={!md||disabledAll}
                          onClick={()=>openConfirm(ft.id, md?.name||'', false)}
                        >
                          {deleting? <CircularProgress size={20}/> : <DeleteIcon/>}
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}

              {/* Campo */}
              {!evtIncludesFieldEvents && (
                <>
                  <TableRow sx={{
                    display: evtIncludesFieldEvents ? 'none' : 'table-row'
                  }}>
                    <TableCell colSpan={4} sx={{ fontStyle:'italic', background:'#fafafa' }}>
                      &mdash; Archivos de Campo &mdash;
                    </TableCell>
                  </TableRow>

                  {[
                    { id:'fldlynx_evt', label:'Eventos Campo (.evt)', accept:'.evt', metadata: fieldEvtMetadata },
                    { id:'fldlynx_sch', label:'Horario Campo (.sch)', accept:'.sch', metadata: fieldSchMetadata }
                  ].map(ft => {
                    const md    = fileMetadata[ft.id];
                    const uploadingField = isUploadingField === ft.id;
                    const deletingField  = isDeleting        === ft.id;
                    const disabledAll    = !!isUploading || !!isDeleting || contextLoading;
                  
                    return (
                      <TableRow key={ft.id} sx={{
                        display: evtIncludesFieldEvents ? 'none' : 'table-row'
                      }}>
                        <TableCell>{ft.label}</TableCell>
                        <TableCell>
                          {md?.url
                            ? <Tooltip title={md.name}>
                                <MuiLink href={md.url} target="_blank">
                                  <CheckIcon color="primary" fontSize="inherit" sx={{ mr: 0.5 }}/>
                                  {md.name}
                                </MuiLink>
                              </Tooltip>
                            : <Typography color="text.secondary">No subido</Typography>
                          }
                          {uploadingField && (
                            <Box mt={0.5}>
                              <LinearProgress variant="determinate" value={fieldUploadProgress} sx={{ height: 3 }} />
                              <Typography variant="caption" textAlign="right">
                                {fieldUploadProgress}%
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {md?.timestamp instanceof Date
                            ? md.timestamp.toLocaleDateString()
                            : '-'
                          }
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" gap={1} justifyContent="flex-end">
                            <Button
                              variant={md ? 'outlined' : 'contained'}
                              size="small"
                              component="label"
                              startIcon={uploadingField ? <CircularProgress size={16}/> : <UploadFileIcon/>}
                              disabled={disabledAll}
                            >
                              {md ? 'Reemplazar' : 'Subir'}
                              <input
                                type="file"
                                accept={ft.accept}
                                hidden
                                onChange={e => handleFieldFileUpload(e, ft.id)}
                              />
                            </Button>
                            <IconButton
                              href={md?.url}
                              target="_blank"
                              disabled={!md?.url || disabledAll}
                            >
                              <DownloadIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              disabled={!md || disabledAll}
                              onClick={() => openConfirm(ft.id, md?.name||'', true)}
                            >
                              {deletingField
                                ? <CircularProgress size={20}/>
                                : <DeleteIcon/>
                              }
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                                  </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Mensajes de acción */}
      <Box mt={2} minHeight={48}>
        {actionError   && <Alert severity="error" icon={<ErrorIcon/>} onClose={()=>setActionError(null)}>{actionError}</Alert>}
        {actionSuccess && <Alert severity="success" icon={<CheckIcon/>} onClose={()=>setActionSuccess(null)}>{actionSuccess}</Alert>}
      </Box>

      {/* Diálogo de confirmación */}
      <Dialog open={confirmOpen} onClose={closeConfirm}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Eliminar <strong>{deleteTarget.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
